// cSpell:ignore Forfait forfait Woninghuurdecreet
// A lease that switched between a forfait and a provision inside a settled
// period (finding N71, 2026-10-19): only its provision days are settled. By
// law a forfait is never settled (art. 36 §3 Woninghuurdecreet and the
// Brussels and Walloon rules alike), so the days a kind was a forfait carry
// no cost and no provision for that tenant; their share stays with the
// owner, the way a whole forfait kind's does (decision 20).
//
// A lease only keeps its current price type. How Rentio billed each field
// over time (GET /settlements/provision-schedule, `priceTypes`) is the only
// trace of the other stretch:
// - a kind that is a forfait today, but whose field Rentio billed as a
//   provision for some days: those days are settled, every other day of the
//   lease stays a forfait;
// - a kind that is a provision today, but whose field Rentio billed as a
//   forfait for some days: those days are not settled.
// Days without any billing follow the lease's current type. A day billed
// both ways (an edit within a month) counts as a provision.
//
// The common kind follows the common-costs field, the private kind the
// non-common field; an extra line is no forfait of its own.

import { ProvisionSlice } from "./provisionHistory";
import { ContractRef } from "./types";

interface IDays {
  from: string;
  to: string;
}

const MS_PER_DAY = 86_400_000;

const shift = (iso: string, days: number): string =>
  new Date(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) + days * MS_PER_DAY)
    .toISOString()
    .slice(0, 10);

const union = (lists: IDays[][]): IDays[] => {
  const sorted = lists
    .flat()
    .map(iv => ({ from: iv.from.slice(0, 10), to: iv.to.slice(0, 10) }))
    .filter(iv => iv.from <= iv.to)
    .sort((a, b) => a.from.localeCompare(b.from));
  const merged: IDays[] = [];
  for (const iv of sorted) {
    const last = merged[merged.length - 1];
    if (last && iv.from <= shift(last.to, 1)) {
      if (iv.to > last.to) last.to = iv.to;
    } else {
      merged.push({ ...iv });
    }
  }
  return merged;
};

const subtract = (intervals: IDays[], remove: IDays[]): IDays[] => {
  let rest = intervals.map(iv => ({ ...iv }));
  for (const r of remove) {
    const next: IDays[] = [];
    for (const iv of rest) {
      if (r.to < iv.from || r.from > iv.to) {
        next.push(iv);
        continue;
      }
      if (r.from > iv.from) next.push({ from: iv.from, to: shift(r.from, -1) });
      if (r.to < iv.to) next.push({ from: shift(r.to, 1), to: iv.to });
    }
    rest = next;
  }
  return rest;
};

const clipTo = (intervals: IDays[], range: IDays): IDays[] =>
  intervals
    .map(iv => ({
      from: iv.from > range.from ? iv.from : range.from,
      to: iv.to < range.to ? iv.to : range.to,
    }))
    .filter(iv => iv.from <= iv.to);

export interface ILeaseFixedFeePeriods {
  // The kinds that are a forfait over the whole lease: not settled at all
  // (see forfaitKindsOf).
  common: boolean;
  private: boolean;
  // Per slice, the days its kind was a forfait while the rest of the lease
  // settles it: only those days are left out. Absent for a kind that is a
  // forfait over the whole lease, or never.
  daysBySlice: Partial<Record<ProvisionSlice, IDays[]>>;
}

const FIELD_OF_SLICE: Record<ProvisionSlice, "common" | "nonCommon"> = {
  common: "common",
  private: "nonCommon",
};

const known = new WeakMap<object, ILeaseFixedFeePeriods>();

export const leaseFixedFeePeriods = (
  contract: Pick<
    ContractRef,
    "startDate" | "endDate" | "fixedFeeCommon" | "fixedFeePrivate"
  > & {
    provisionSchedule?: ContractRef["provisionSchedule"];
  },
): ILeaseFixedFeePeriods => {
  const cached = known.get(contract);
  if (cached) return cached;
  const lease = {
    from: contract.startDate.slice(0, 10),
    to: contract.endDate.slice(0, 10),
  };
  const stretches = contract.provisionSchedule?.priceTypes ?? [];
  const out: ILeaseFixedFeePeriods = {
    common: !!contract.fixedFeeCommon,
    private: !!contract.fixedFeePrivate,
    daysBySlice: {},
  };
  for (const slice of ["common", "private"] as ProvisionSlice[]) {
    const field = FIELD_OF_SLICE[slice];
    const ofField = stretches.filter(stretch => stretch.field === field);
    const provisionDays = clipTo(
      union([ofField.filter(stretch => stretch.priceType === "provision")]),
      lease,
    );
    const isFixedFeeToday = out[slice];
    if (isFixedFeeToday) {
      // A forfait today, unless Rentio billed some of its days as a
      // provision: those days are settled, the rest of the lease is not.
      if (provisionDays.length === 0) continue;
      out[slice] = false;
      const fixedFeeDays = subtract([lease], provisionDays);
      if (fixedFeeDays.length > 0) out.daysBySlice[slice] = fixedFeeDays;
      continue;
    }
    const fixedFeeDays = subtract(
      clipTo(
        union([ofField.filter(stretch => stretch.priceType === "fixedFee")]),
        lease,
      ),
      provisionDays,
    );
    if (fixedFeeDays.length > 0) out.daysBySlice[slice] = fixedFeeDays;
  }
  known.set(contract, out);
  return out;
};

// How many of `days` fall within `range` (both inclusive ISO days).
export const daysWithin = (days: IDays[], range: IDays): number =>
  clipTo(days, range).reduce(
    (sum, iv) =>
      sum +
      Math.round(
        (Date.parse(`${iv.to}T00:00:00Z`) -
          Date.parse(`${iv.from}T00:00:00Z`)) /
          MS_PER_DAY,
      ) +
      1,
    0,
  );

// The days each kind of a lease was a forfait while its other days are
// settled (N71), as the engine reads them from the lease's billing history
// (leaseForfaitPeriods); for a lease read without that history, the days
// the engine froze within the period (PerContractResult.
// forfaitIntervalsBySlice). The summary and the statement count none of them.
export const fixedFeeDaysOfLease = (
  contract: Parameters<typeof leaseFixedFeePeriods>[0] | undefined,
  frozen: Partial<Record<ProvisionSlice, IDays[]>> | undefined,
): Partial<Record<ProvisionSlice, IDays[]>> => {
  const daysBySlice = contract
    ? leaseFixedFeePeriods(contract).daysBySlice
    : {};
  return Object.keys(daysBySlice).length > 0 ? daysBySlice : frozen ?? {};
};

// How many days a lease ran within `range` while its kind was not a
// forfait: the days its share of a cost of that kind counts (N71).
export const settledLeaseDaysWithin = (params: {
  lease: IDays;
  range: IDays;
  fixedFeeDays?: IDays[];
}): number => {
  const from =
    params.lease.from.slice(0, 10) > params.range.from.slice(0, 10)
      ? params.lease.from.slice(0, 10)
      : params.range.from.slice(0, 10);
  const to =
    params.lease.to.slice(0, 10) < params.range.to.slice(0, 10)
      ? params.lease.to.slice(0, 10)
      : params.range.to.slice(0, 10);
  if (from > to) return 0;
  const leaseDays = daysWithin([{ from, to }], { from, to });
  return Math.max(
    0,
    leaseDays - daysWithin(params.fixedFeeDays ?? [], { from, to }),
  );
};
